import { useMemo, useState } from 'react';
import { Box, IconButton, Popover, Tooltip, Typography, Chip } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { authFetch } from '../utils/authHelpers';
import { AI_ENGINE_URL } from '../utils/apiConfig';

/**
 * ⓘ-knopje met de wijzigingslog: wie wijzigde wat, wanneer, van wat naar wat.
 *
 * Eén component voor alle schermen. Geef `projectId` voor alles binnen een
 * project (Overzicht per project, projectpagina) of `entityId` voor één taak,
 * deliverable of draaidag (dialoog, drawer). De data komt uit status_audit_log
 * via de engine; de log loopt sinds 2 sep 2026.
 */
const FONT = '"Manrope", system-ui, sans-serif';
const SOORT_LABEL = { task: 'Taak', deliverable: 'Deliverable', project: 'Project', draaidag: 'Draaidag' };
const SOORT_KLEUR = { task: '#dbeafe', deliverable: '#fef3c7', project: '#ede9fe', draaidag: '#dcfce7' };

const wieVanMail = (email) => {
  const lokaal = String(email || '').split('@')[0];
  if (!lokaal) return 'systeem';
  return lokaal.split(/[._-]/).map((d) => d.charAt(0).toUpperCase() + d.slice(1)).join(' ');
};
const tijdKort = (iso) => (iso ? new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }) : '');
const dagKop = (iso) => {
  const d = new Date(iso); const nu = new Date();
  const zelfde = (a, b) => a.toDateString() === b.toDateString();
  if (zelfde(d, nu)) return 'Vandaag';
  const gisteren = new Date(nu); gisteren.setDate(nu.getDate() - 1);
  if (zelfde(d, gisteren)) return 'Gisteren';
  return d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', year: d.getFullYear() !== nu.getFullYear() ? 'numeric' : undefined });
};
const isGebeurtenis = (r) => ['aangemaakt', 'verwijderd'].includes(String(r.field || '').toLowerCase());

export default function WijzigingslogKnop({ projectId, entityId, titel, size = 16, sx = {}, renderKnop }) {
  const [anchor, setAnchor] = useState(null);
  const [log, setLog] = useState(null);
  const [fout, setFout] = useState(null);
  const [soort, setSoort] = useState('');

  const open = async (e) => {
    e.stopPropagation?.(); e.preventDefault?.();
    setAnchor(e.currentTarget); setLog(null); setFout(null); setSoort('');
    const pad = entityId
      ? `/api/v1/airtable/status-log/entity/${encodeURIComponent(entityId)}?limit=200`
      : `/api/v1/airtable/status-log/project/${encodeURIComponent(projectId)}?limit=300`;
    try {
      const r = await authFetch(`${AI_ENGINE_URL}${pad}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.error || `HTTP ${r.status}`);
      setLog(d.log || []);
    } catch (err) { setFout(err.message); setLog([]); }
  };

  const soorten = useMemo(() => [...new Set((log || []).map((r) => r.entity_type))].filter(Boolean), [log]);
  const zichtbaar = useMemo(() => (log || []).filter((r) => !soort || r.entity_type === soort), [log, soort]);
  const perDag = useMemo(() => {
    const m = new Map();
    zichtbaar.forEach((r) => { const k = String(r.at || '').slice(0, 10); if (!m.has(k)) m.set(k, []); m.get(k).push(r); });
    return [...m.entries()];
  }, [zichtbaar]);

  const knop = renderKnop ? renderKnop(open) : (
    <Tooltip title="Wijzigingslog: wie wijzigde wat, wanneer">
      <IconButton size="small" onClick={open} aria-label="Wijzigingslog" sx={{ color: '#94a3b8', '&:hover': { color: '#526075', bgcolor: '#e2e8f0' }, ...sx }}>
        <HistoryIcon sx={{ fontSize: size }} />
      </IconButton>
    </Tooltip>
  );

  return (
    <>
      {knop}
      <Popover open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 580, maxWidth: '95vw', maxHeight: 520, borderRadius: 2, boxShadow: '0 12px 32px rgba(15,23,42,0.16)', border: '1px solid #e2e8f0' } } }}
        onClick={(e) => e.stopPropagation()}>
        <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13 }}>Wijzigingen</Typography>
          {titel && <Typography noWrap sx={{ fontSize: 12, color: '#94a3b8', flex: 1 }}>{titel}</Typography>}
          {!titel && <Box sx={{ flex: 1 }} />}
          {log && <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{log.length >= 300 ? 'laatste 300' : `${log.length} regels`}</Typography>}
        </Box>
        {soorten.length > 1 && (
          <Box sx={{ display: 'flex', gap: 0.5, px: 2, py: 0.75, borderBottom: '1px solid #f8fafc', flexWrap: 'wrap' }}>
            <Chip size="small" label="Alles" onClick={() => setSoort('')} variant={soort ? 'outlined' : 'filled'} sx={{ height: 22, fontSize: 11 }} />
            {soorten.map((s) => (
              <Chip key={s} size="small" label={SOORT_LABEL[s] || s} onClick={() => setSoort(s)} variant={soort === s ? 'filled' : 'outlined'}
                sx={{ height: 22, fontSize: 11, bgcolor: soort === s ? SOORT_KLEUR[s] : undefined }} />
            ))}
          </Box>
        )}
        <Box sx={{ overflowY: 'auto', maxHeight: 430, px: 2, py: 1 }}>
          {log === null && <Typography sx={{ fontSize: 12.5, color: '#94a3b8', py: 2, textAlign: 'center' }}>Laden…</Typography>}
          {fout && <Typography sx={{ fontSize: 12.5, color: '#b91c1c', py: 1 }}>{fout}</Typography>}
          {log && !log.length && !fout && (
            <Typography sx={{ fontSize: 12.5, color: '#94a3b8', py: 2, textAlign: 'center', lineHeight: 1.5 }}>
              Nog geen wijzigingen vastgelegd. Sinds 2 september 2026 wordt elke wijziging aan taken, deliverables, projecten en draaidagen hier bijgehouden; eerdere wijzigingen staan alleen in de Airtable-historie.
            </Typography>
          )}
          {perDag.map(([dag, rijen]) => (
            <Box key={dag} sx={{ mb: 1.25 }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.5 }}>{dagKop(rijen[0].at)}</Typography>
              {rijen.map((r, i) => (
                <Box key={`${r.at}-${i}`} sx={{ display: 'grid', gridTemplateColumns: '42px 84px 1fr', gap: 1, py: 0.6, borderTop: '1px solid #f8fafc', alignItems: 'baseline' }}>
                  <Typography sx={{ fontSize: 11.5, color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{tijdKort(r.at)}</Typography>
                  <Typography noWrap sx={{ fontSize: 12, fontWeight: 700, color: '#334155' }} title={r.user_email}>{wieVanMail(r.user_email)}</Typography>
                  <Typography sx={{ fontSize: 12, color: '#334155', lineHeight: 1.45 }}>
                    {!entityId && r.entity_type && (
                      <Box component="span" sx={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', bgcolor: SOORT_KLEUR[r.entity_type] || '#f1f5f9', color: '#475569', borderRadius: '4px', px: 0.6, py: 0.1, mr: 0.75 }}>
                        {SOORT_LABEL[r.entity_type] || r.entity_type}
                      </Box>
                    )}
                    {isGebeurtenis(r) ? (
                      <>
                        <Box component="span" sx={{ fontWeight: 700 }}>{r.field}</Box>
                        {r.entity_name && <>{': '}<Box component="span" sx={{ fontWeight: 600 }}>{r.entity_name}</Box></>}
                        {r.new_value && <Box component="span" sx={{ color: '#94a3b8' }}>{' · '}{r.new_value}</Box>}
                      </>
                    ) : (
                      <>
                        <Box component="span" sx={{ color: '#64748b' }}>{r.field}</Box>
                        {' van '}<Box component="span" sx={{ textDecoration: 'line-through', color: '#94a3b8' }}>{r.old_value || '—'}</Box>
                        {' naar '}<Box component="span" sx={{ fontWeight: 700 }}>{r.new_value || '—'}</Box>
                        {!entityId && r.entity_name && <Box component="span" sx={{ color: '#94a3b8' }}>{' · '}{r.entity_name}</Box>}
                      </>
                    )}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
}
